import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { RotateCcw, Trash2, User, FileText } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useConfirm } from "@/context/ConfirmContext";

const REASON = { self: "U fshi vet", owner: "E fshiu pronari", admin: "E fshiu admini" };

function TrashRow({ t, onRestore, onPurge }) {
  return (
    <div data-testid={`trash-item-${t.id}`} className="bg-white rounded-2xl border border-slate-100 p-3 flex items-center gap-3">
      <div className="w-11 h-11 rounded-xl bg-slate-100 overflow-hidden flex items-center justify-center shrink-0 text-slate-400">
        {t.image ? <img src={t.image} alt="" className="w-full h-full object-cover" /> : t.kind === "user" ? <User className="w-5 h-5" /> : <FileText className="w-5 h-5" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-800 break-words" translate="no">
          {t.kind === "user" && t.member_no != null && <span className="text-[10px] font-extrabold text-emerald-700 bg-emerald-50 px-1.5 rounded mr-1">#{String(t.member_no).padStart(2, "0")}</span>}
          {t.title || "—"}
        </div>
        <div className="text-[11px] text-slate-500">
          {t.kind === "user" ? `Përdorues · ${t.email}${t.listings_count ? ` · ${t.listings_count} shpallje` : ""}` : `Shpallje · ${t.category}${t.city ? ` · ${t.city}` : ""}`}
        </div>
        <div className="text-[11px] text-slate-400">{REASON[t.reason] || ""}{t.deleted_by_name && t.reason !== "self" ? ` (${t.deleted_by_name})` : ""} · <span data-testid={`trash-days-${t.id}`} className="text-amber-600 font-semibold">{t.days_left} ditë mbetur</span></div>
      </div>
      <div className="flex flex-col gap-1 shrink-0">
        <button data-testid={`trash-restore-${t.id}`} onClick={() => onRestore(t)} className="h-8 px-3 rounded-full bg-emerald-500 text-white text-xs font-bold flex items-center gap-1"><RotateCcw className="w-3.5 h-3.5" />Kthe</button>
        <button data-testid={`trash-purge-${t.id}`} onClick={() => onPurge(t)} className="h-8 px-3 rounded-full bg-rose-50 text-rose-600 text-xs font-bold flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" />Fshi</button>
      </div>
    </div>
  );
}

export default function AdminTrash() {
  const { confirm } = useConfirm();
  const [items, setItems] = useState(null);
  const [kind, setKind] = useState("all");
  const load = () => api.get("/admin/trash").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const restore = async (t) => {
    try { await api.post(`/admin/trash/${t.id}/restore`); toast.success(t.kind === "user" ? "Përdoruesi u kthye bashkë me shpalljet" : "Shpallja u kthye"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const purge = async (t) => {
    if (!(await confirm({ title: "Fshi përgjithmonë", message: `“${t.title}” do të fshihet përgjithmonë dhe nuk mund të kthehet më.`, danger: true }))) return;
    try { await api.delete(`/admin/trash/${t.id}`); toast.success("U fshi përgjithmonë"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (!items) return <div className="p-6 text-center text-sm text-slate-500">Duke ngarkuar...</div>;
  const shown = items.filter((t) => kind === "all" || t.kind === kind);
  return (
    <div className="space-y-2" data-testid="admin-trash">
      <p className="text-xs text-slate-500">Çdo shpallje ose përdorues i fshirë ruhet këtu 60 ditë. Pastaj fshihet vetë.</p>
      <div className="flex gap-2">
        {[["all", "Të gjitha"], ["listing", "Shpallje"], ["user", "Përdorues"]].map(([k, l]) => (
          <button key={k} data-testid={`trash-filter-${k}`} onClick={() => setKind(k)} className={`h-8 px-3 rounded-full text-xs font-semibold border ${kind === k ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-200"}`}>{l}</button>
        ))}
      </div>
      {shown.length === 0 && <div data-testid="trash-empty" className="text-center text-sm text-slate-400 py-8">Koshi është bosh</div>}
      {shown.map((t) => <TrashRow key={t.id} t={t} onRestore={restore} onPurge={purge} />)}
    </div>
  );
}
